// Editorial tile-gallery series picker. Photographic tiles fill the viewport;
// hovering a tile expands it relative to its siblings (flex: 1.4 vs. 1) and
// fades in the body copy. Mobile collapses to a vertical stack of full-bleed
// tiles. Scoped CSS lives under .sd--layout-single-menu.
//
// Chrome copy (top-bar centre title + intro eyebrow/headline/body) is rendered
// from the series-picker-topbar and series-picker-intro slots, so Designers
// can edit it through the slot mapping editor with DVar fields like any other
// text block. Falls back to a small hardcoded English baseline when the host
// context isn't supplied (defensive — should never happen for single-menu).

import { SeriesContext } from "../blocks/SeriesTileBlocks";
import { mergeSlotMappings } from "../blocks/slotMapping";
import { Slot } from "../blocks/Slot";
import { SINGLE_MENU_DEFAULT_MAPPING } from "../layouts/SingleMenuLayout.defaults";
import type { BlockHostContext, SlotMapping } from "../blocks/types";
import type { DesignerSeriesDto } from "../types";

export interface SingleMenuSeriesPickerProps {
  series: DesignerSeriesDto[];
  onPick: (s: DesignerSeriesDto) => void;
  designerName?: string;
  brandLogoUrl?: string | null;
  /** Block host context — needed for <Slot>. Required for the editorial
   *  picker since its chrome copy is slot-driven. */
  blockHost?: BlockHostContext;
  /** Designer override of the slot mapping, merged over the single-menu
   *  defaults at render time. */
  slotMappingOverride?: SlotMapping | null;
}

export function SingleMenuSeriesPicker({
  series,
  onPick,
  designerName,
  brandLogoUrl,
  blockHost,
  slotMappingOverride,
}: SingleMenuSeriesPickerProps) {
  // Same merge semantics as the configurator layout uses for its own slots.
  const mapping = mergeSlotMappings(
    SINGLE_MENU_DEFAULT_MAPPING,
    slotMappingOverride ?? null,
  );
  // The tile's CONTENT is operator-composed: the series-picker-tiles slot's
  // blocks render once per series, bound to that series via SeriesContext (the
  // series-image / series-name blocks read it; a text block is the CTA). The
  // wrapper below owns the tile frame + click-to-pick + hover.
  const tileBlocks = mapping["series-picker-tiles"];

  return (
    <div className="sd-eg-series">
      <header className="sd-eg-series__topbar">
        <div className="sd-eg-series__brand">
          {brandLogoUrl ? (
            <img src={brandLogoUrl} alt={designerName ?? "Brand"} />
          ) : (
            designerName && <span className="sd-eg-series__brand-text">{designerName}</span>
          )}
        </div>
        <div className="sd-eg-series__topbar-title">
          {blockHost ? (
            <Slot
              slotId="series-picker-topbar"
              mapping={mapping["series-picker-topbar"]}
              host={blockHost}
              inline
            />
          ) : (
            <span>Choose a series</span>
          )}
        </div>
        <span className="sd-eg-series__topbar-spacer" />
      </header>

      {blockHost ? (
        <Slot
          slotId="series-picker-intro"
          mapping={mapping["series-picker-intro"]}
          host={blockHost}
          className="sd-eg-series__intro"
        />
      ) : (
        <div className="sd-eg-series__intro">
          <p className="sd-eg-series__eyebrow">{designerName ?? "Configurator"}</p>
          <h1 className="sd-eg-series__title">
            Begin with a <em>system</em>.
          </h1>
          <p className="sd-eg-series__body">
            Pick a starting point.
          </p>
        </div>
      )}

      <ul className="sd-eg-series__tiles">
        {series.map(s => (
          <li key={s.id} className="sd-eg-series__tile">
            <button
              type="button"
              className="sd-eg-series__tile-btn"
              onClick={() => onPick(s)}
              aria-label={s.label}
            >
              {/* The tile content is the series-picker-tiles slot rendered for
                  THIS series. SeriesContext binds the series so the series-image
                  / series-name blocks resolve it. When the host context isn't
                  available (defensive), fall back to the bare image + name. */}
              {blockHost && tileBlocks && tileBlocks.length > 0 ? (
                <SeriesContext.Provider value={s}>
                  <Slot
                    slotId="series-picker-tiles"
                    mapping={tileBlocks}
                    host={blockHost}
                    inline
                  />
                </SeriesContext.Provider>
              ) : (
                <>
                  <div className="sd-eg-series__tile-img">
                    {s.heroImageUrl ?? s.iconUrl ? (
                      <img src={(s.heroImageUrl ?? s.iconUrl)!} alt="" draggable={false} />
                    ) : (
                      <div className="sd-eg-series__tile-img-empty" />
                    )}
                  </div>
                  <span className="sd-eg-series__tile-name">{s.label}</span>
                </>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
